import { queryClient } from "@shared/api/queries/queryClient";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { StepHelpers } from "@shared/components/Stepper";
import { Button } from "@shared/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@shared/ui/card";
import { Tabs } from "@shared/ui/tabs";
import { CreditCard, FileText, Users } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useBulkBillData,
  usePlanDashboard,
  useUpdateConsumerSelection,
} from "../../hooks";
import { ConsumerBreakdownTab } from "../fetch-consumers-tab/ConsumerBreakdownTab";
import { PaymentDetailsTab } from "../fetch-consumers-tab/PaymentDetailsTab";
import { PlansTariffsTab } from "../fetch-consumers-tab/PlansTariffsTab";

interface FetchConsumerStepProps {
  // Stepper-injected props
  onNext?: () => void;
  onPrevious?: () => void;
  currentStepIndex?: number;
  stepHelpers?: StepHelpers;
  setValidationError?: (error: string) => void;
  clearValidationError?: () => void;
  isValidating?: boolean;
  isFirstStep?: boolean;
  isLastStep?: boolean;
}

const FetchConsumerStep: React.FC<FetchConsumerStepProps> = ({
  onNext,
  onPrevious,
  currentStepIndex = 1,
  stepHelpers,
  setValidationError,
  clearValidationError,
  isValidating,
  isFirstStep,
  isLastStep,
}) => {
  const navigate = useNavigate();
  const { id, action = "add" } = useParams();
  const [excludedServiceCharges, setExcludedServiceCharges] = useState<
    number[]
  >([]);
  const dataLoadedRef = useRef(false);
  const { data: bulkBillData } = useBulkBillData(id);
  const { mutateAsync: updateConsumer, isPending: isUpdating } =
    useUpdateConsumerSelection(stepHelpers.getStepData(0)?.draftId || id);
    const billDraftId=stepHelpers.getStepData(0)?.draftId;
  // Helper functions for stepHelpers
  const saveStepData = useCallback(
    (data: any) => {
      if (!stepHelpers) return;
      try {
        const stepData = {
          ...data,
          timestamp: new Date().toISOString(),
          stepIndex: currentStepIndex,
        };
        stepHelpers.setStepData(currentStepIndex, stepData);
      } catch (error) {
        console.error("Failed to save step data:", error);
      }
    },
    [stepHelpers, currentStepIndex]
  );

  const getStepData = useCallback(() => {
    if (!stepHelpers) return null;
    try {
      return stepHelpers.getStepData(currentStepIndex);
    } catch (error) {
      console.error("Failed to get step data:", error);
      return null;
    }
  }, [stepHelpers, currentStepIndex]);

  // Get bill cycle ID from previous step (replaces params?.id)
  const getBillCycleId = useCallback(() => {
    if (!stepHelpers) return null;

    try {
      console.log("stepHelpers.getDataFromStep", stepHelpers.getStepData(0));
      // Get from previous step (step 0)
      return (
        stepHelpers.getDataFromStep(0, "billCycleId") ||
        stepHelpers.getDataFromStep(0, "workflowBillCycleId") ||
        bulkBillData?.billScheduleData?.id
      );
    } catch (error) {
      console.error("Failed to get bill cycle ID:", error);
      return null;
    }
  }, [stepHelpers, bulkBillData]);

  const billCycleId = getBillCycleId();

  // Use the plan dashboard hook (same as before, just with billCycleId instead of params?.id)
  const {
    data: planData,
    isPending: isPlanLoading,
    error: planError,
  } = usePlanDashboard(billCycleId);

  // Load saved data from stepHelpers on component mount
  useEffect(
    () => {
      if (stepHelpers) {
        try {
          const savedStepData = getStepData();
          if (savedStepData) {
            // Restore state from saved data
            if (savedStepData.excludedServiceCharges) {
              setExcludedServiceCharges(savedStepData.excludedServiceCharges);
            }
          }
        } catch (error) {
          console.error("Failed to load step data:", error);
        }
      }
    },
    [
      // stepHelpers,
      // getStepData
    ]
  );

  // Save state changes to stepHelpers (only when excludedServiceCharges changes)
  useEffect(() => {
    if (stepHelpers && !dataLoadedRef.current) {
      try {
        const currentData = getStepData() || {};
        const updatedData = {
          ...currentData,
          excludedServiceCharges,
          billCycleId,
          lastUpdated: new Date().toISOString(),
        };
        saveStepData(updatedData);
        dataLoadedRef.current = true;
      } catch {
        dataLoadedRef.current = true;
      }
    }
  }, [
    excludedServiceCharges,
    billCycleId,
    saveStepData,
    getStepData,
    stepHelpers,
  ]);

  // Keep original tabConfig exactly as it was
  const tabConfig = {
    "consumer-breakdown": {
      label: "Consumer Breakdown",
      icon: <Users className="h-4 w-4" />,
      component: <ConsumerBreakdownTab billCycleId={billCycleId} billDraftId={billDraftId} />,
    },
    "plans-tariffs": {
      label: "Plans & Tariffs",
      icon: <FileText className="h-4 w-4" />,
      component: <PlansTariffsTab billCycleId={billCycleId} billDraftId={billDraftId} />,
    },
    "payment-details": {
      label: "Payment Details",
      icon: <CreditCard className="h-4 w-4" />,
      component: <PaymentDetailsTab billCycleId={billCycleId} />,
    },
  };

  // Replace navigateToStep with stepper navigation
  const handleContinue = async () => {
    // Save final step data
    const finalStepData = {
      excludedServiceCharges,
      billCycleId,
      isStepComplete: true,
      completedAt: new Date().toISOString(),
    };

    const payload = {
      step: {
        step_working_on: 1,
        total_steps: 5,
      },
    };
    const response = await updateConsumer(payload);

    if (response) {
      saveStepData(finalStepData);

      // Use stepper navigation instead of manual navigation
      if (onNext) {
        onNext();
      }
    }
  };

  const handleDraft = async () => {
    // Save final step data
    const finalStepData = {
      excludedServiceCharges,
      billCycleId,
      isStepComplete: true,
      completedAt: new Date().toISOString(),
    };

    const payload = {
      step: {
        step_working_on: 1,
        total_steps: 5,
      },
    };
    const response = await updateConsumer(payload);

    if (response) {
      saveStepData(finalStepData);
      navigate("/bx/history?tab=bulk&tab1=draft");
      await queryClient.refetchQueries({
        queryKey: [QueryKeyFactory.module.bx.billHistory.BulkBillDraft(), 1],
        exact: false,
      });
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="page-subheading">
          Consumer Data Analysis
        </h3>
        <p className="page-subtitle">
          Analyze consumer data, plans, and tariffs for the selected billing
          cycle
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle>Consumer Overview</CardTitle>
            <CardDescription>Total consumers in current cycle</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="card-value-text font-bold">
              {planData?.totalConsumerCount ?? "NA"}
              <span className="text-sm font-normal ml-2 text-success">
                ({planData?.totalIncreasePercentage ?? "NA"}
                %)
              </span>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle>Missing Plan Details</CardTitle>
            <CardDescription>Consumers with incomplete plans</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="card-value-text">
              {planData?.missingPlanCount}
              <span className="text-sm font-normal ml-2 text-warning">
                Action Required
              </span>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle>Missing Tariff Details</CardTitle>
            <CardDescription>Plans without assigned tariffs</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="card-value-text">
              {planData?.missingTarrifCount}
              <span className="text-sm font-normal ml-2 text-warning">
                Action Required
              </span>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs
        defaultValue="consumer-breakdown"
        tabComponents={tabConfig}
        tabsListClassName="grid w-full grid-cols-3"
        level={0}
        disableUrlSync={true}
      />

      <div className="flex justify-between mt-6">
        <Button onClick={onPrevious} disabled={isFirstStep}>
          Previous
        </Button>
        <div className="flex gap-4">
          <Button onClick={handleDraft}>Save As Draft</Button>
          <Button onClick={handleContinue}>Next: Meter Readings</Button>
        </div>
      </div>
    </div>
  );
};

export default FetchConsumerStep;
